import { CommentOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Collapse, Drawer, Image, Input, Statistic, Table, message } from 'antd';
import { debounce, isEqual } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Await, Link, defer, generatePath, redirect, useFetcher, useLoaderData } from 'react-router-dom';
import { getProductDetails } from '../../../api/product';
import { addItemCustomSpecification, addItemHiddenSpecification, assignFinisher, assignProjectItemVendor, declineAlert, deleteItem, deleteItemCustomSpecification, deleteItemHiddenSpecification, getItemOrderSalesCommentHistory, getItemPrices, getProjectItem, projectItemVendorQuotationComment, removeProjectItemVendor, sendCommentToOrderManager, sendVendorProjectItemQuotationRequest, sortItemCustomSpecifications, unassignFinisher, updateItemCustomSpecification, updateProjectItem, updateProjectItemSpecifications, updateProjectItemVendorPrice } from '../../../api/project-item';
import { CommunicationPanel } from '../../../components/communication-panel';
import { CmsPricesSelectable, ProjectItemSpecifications } from '../../../components/data';
import { ValidationForm as Form, ProjectItem, VendorItemPrices } from '../../../components/form';
import { FieldWrapper } from '../../../components/inputs/input-wrappers.jsx';
import { Page } from '../../../components/page';
import { ValidationError } from '../../../library/classes/exceptions/index.js';
// import { generateRouteUrl } from '../../../library/constants/routes.js';
import { uploadFileFromObject } from '../../../api/common/image-upload.js';
import { addItemDeliveryAddress, bulkItemDeliveryAddress, deleteBulkDeliveryFile, deleteItemDeliveryAddress, updateItemDeliveryAddress } from '../../../api/project-item/item-delivery-addresses.js';
import { getProjectDetails, getProjectItemActivityLogs } from '../../../api/project/project.js';
import { ActionCenter } from '../../../components/design/action-center.jsx';
import { SelectQuantites } from '../../../components/inputs/select-quantities.jsx';
import Confirm from '../../../components/message/Confirm.jsx';
import { ItemEditableOrderStatuses, OrderStatuses } from '../../../library/constants/OrderStatuses.js';
import { ERP_CURRENCY } from '../../../library/constants/dynamic.js';
import { ItemUpdateAuditTrailColumns } from '../../../library/constants/index.js';
import { ProjectStatuses, getProjectStatusByID } from '../../../library/constants/project-statuses.js';
import { generateRouteUrl } from '../../../library/constants/routes.js';
import { getFieldStatus } from '../../../library/helpers/forms/index.js';
import { convertDataTypes, getFormattedId } from '../../../library/helpers/index.js';
import { getProductImageUrl } from '../../../library/helpers/product_url.js';
import { isObjectEmpty } from '../../../library/utilities/common.js';
import { ItemAlerts } from './item-alerts.jsx';
import { DeliveryAddresses } from './item-delivery-address.jsx';
import { PrintFinisherInvolved } from './print-finisher-involved.jsx';
const { Panel } = Collapse

const ProjectItemUpdate = ({ title }) => {
	const { itemId, itemDetails, basicItemDetails, productDetails, selectedQuantities, productPrices, language, orderCommunicationHistory, project, auditTrail } = useLoaderData()
	const [showPricesDrawer, SetShowPricesDrawer] = useState(false)
	const [commentsForm] = Form.useForm()
	const fetcher = useFetcher()
	const specsFetcher = useFetcher()
	const canUpdateQuotation = useMemo(() => project.status < ProjectStatuses.QUOTATION_SENT, [project.status])
	const canUpdateSpecs = useMemo(() => {
		return (
			([ProjectStatuses.NEW, ProjectStatuses.REQUEST_SEND, ProjectStatuses.CONFIRMATION_CLIENT_SENT].includes(project.status) && (
				!itemDetails.order_status ||
				ItemEditableOrderStatuses.includes(itemDetails.order_status)
			))
		)
	}, [project.status])

	const isApproved = itemDetails.active === 1
	const isRejected = itemDetails.active === 2
	const isCancelled = itemDetails.order_status === OrderStatuses.CANCELED

	const nonUpdatable = useMemo(() => (
		[ProjectStatuses.CANCELED, ProjectStatuses.CLOSED_LOST].includes(project.status) ||
		isRejected ||
		isCancelled
	), [project.status])
	// const { focusLast } = useInputFocusManager()

	// useEffect(() => {
	// 	focusLast()
	// })


	const getSpecifications = () => {
		/**
			* Get optimistic data while specs are being updated.
			* When specs are being submitted, data is not updated in ui,
			* So we get data from specsFetcher if availale instead of actual data
			*/
		if (specsFetcher.json?.action === 'updateSelectedSpecs' && specsFetcher.json.selectedSpecs) {
			const specs = productDetails.specifications
				.filter(spec => !!specsFetcher.json.selectedSpecs[spec.id]?.valueId)
				.map(spec => ({
					id: spec.id,
					name: spec.name,
					sort_order: spec.sort_order,
					text: spec.text,
					value: spec.values.find(({ id }) => id === specsFetcher.json.selectedSpecs[spec.id]?.valueId)
				}))
			return specs
		}

		if (!specsFetcher.json?.action === 'sortSpecifications' || !specsFetcher.json?.selectedSpecs) {
			return Object.values(itemDetails.specifications)
		}

		return itemDetails.specifications.map(val => ({
			...val,
			sort_order: specsFetcher.json.selectedSpecs?.[val.id]?.sort_order
		}))
	}

	const updateItemDetails = debounce((_, values) => {
		fetcher.submit({
			...values,
			action: 'updateItem'
		}, {
			method: 'post',
			encType: 'application/json',
		})
	}, 1000)

	const updateVendorPrices = (vendorId, quantity, prices) => {
		fetcher.submit({
			...prices,
			vendorId,
			quantity,
			action: 'updateVendorPrice'
		}, {
			method: 'post',
			encType: 'application/json',
		})
	}

	const saveSelectedSpecs = (selectedSpecs) => {
		specsFetcher.submit({
			selectedSpecs,
			action: 'updateSelectedSpecs'
		}, {
			method: 'post',
			encType: 'application/json'
		})
	}

	const sortSpecifications = (sortSpecs, selectedSpecs) => {
		specsFetcher.submit({
			sortSpecs,
			selectedSpecs,
			action: 'sortSpecifications'
		}, {
			method: 'post',
			encType: 'application/json'
		})
	}

	const handleQuantitiesChange = (quantities) => {
		fetcher.submit({
			quantities: quantities.sort((a, b) => a - b),
			action: 'updateItem'
		}, {
			method: 'post',
			encType: 'application/json',
		})
	}

	const handleVendorSpecialCommentChange = debounce((vendorId, values) => {
		projectItemVendorQuotationComment(itemId, vendorId, values.comment)
	}, 1000)

	return (
		<Page className='projects-item-create' title={title} backLink={generatePath('/projects/:id', {
			id: itemDetails.project_id
		})} header={(
			<>
				<div className='headerStats'>
					<Statistic
						title="Project ID"
						value={getFormattedId(itemDetails.project_id)}
						formatter={v => v}
						valueStyle={{
							color: '#5A5A5A',
							backgroundColor: '#E6E6E6'
						}}
					/>
					<Statistic
						title="Project Status"
						value={getProjectStatusByID(project.status)?.name}
					/>
					{(isApproved || isRejected || isCancelled) && (
						<Statistic
							title="Item Status"
							value={isCancelled ? 'Cancelled' : isRejected ? 'Not Approved' : 'Approved'}
						/>
					)}
				</div>
				{canUpdateQuotation && (
					<Confirm content="Are you sure you want to delete this item ?This action cannot be undone" onConfirm={() => fetcher.submit({
						itemId,
						action: 'deleteItem'
					}, {
						method: 'post',
						encType: 'application/json'
					})} type="primary" loading={fetcher.json?.action === 'deleteItem'} danger>Delete Item</Confirm>
				)}
			</>
		)}>
			<div style={{
				display: 'flex',
				flexDirection: 'column',
				alignTtems: 'stretch',
			}} >
				<ItemAlerts projectId={project.id} itemId={itemId} alerts={itemDetails.active_alert} />
				<ActionCenter actions={[
					{
						action: (
							<Link to={generateRouteUrl('AdditionalQuotation', { projectId: itemDetails.project_id, itemId })}>
								<Button shape="square" type="actionpanel" ghost>Additional Quotation</Button>
							</Link>
						),
						isVisible: (ProjectStatuses.CONFIRMATION_CLIENT_SENT === project.status && itemDetails.active === 1)
					}
				]} />
				<Collapse defaultActiveKey={['1']} expandIconPosition="end">
					<Panel header={<><img src='/icons/headericons/iteminformation.svg' style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Item Information</>} key="1">
						<ProjectItem
							productUrl={itemDetails.product_url_spec}
							initialValues={basicItemDetails}
							onValuesChange={updateItemDetails}
							product={productDetails}
							language={language}
							canChangeProduct={!!itemDetails.is_product_changable}
							isUpdate
							disabled={nonUpdatable}
						/>
					</Panel>
				</Collapse>
				<Collapse defaultActiveKey={['1']} expandIconPosition="end">
					<Panel header={<><img src='/icons/headericons/specification.svg' style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Specifications</>} key="1">
						<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2em' }}>
							<Image
								src={getProductImageUrl(productDetails.main_photos?.[0] ?? productDetails.photo)}
								style={{ borderRadius: '15px', maxHeight: '50vh', maxWidth: '50vw' }}
								fallback="data:image/png;base64,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"
							/>
							<ProjectItemSpecifications
								key="prjectitemspecs"
								itemId={itemId}
								inbuiltSpecifications={productDetails.specifications}
								customSpecifications={fetcher.json?.sortSpecs || itemDetails?.item_custom_specifications}
								hiddenSpecifications={productDetails.hiddenspecifications}
								hiddenSpecificationValues={itemDetails.item_hidden_specifications}
								specificationValues={getSpecifications()}
								max_print_area_size={itemDetails.max_print_area_size}
								print_position={itemDetails.print_position}
								onSpecificationsChange={(selectedSpecs) => {
									saveSelectedSpecs(selectedSpecs)
								}}
								onCustomSpecificationAdd={async (data) => {
									specsFetcher.submit({
										...data,
										action: 'customSpecification'
									}, {
										encType: 'application/json',
										method: 'post'
									})
								}}
								onCustomSpecificationUpdate={async (specId, data) => {
									specsFetcher.submit({
										...data,
										specId: specId,
										action: 'customSpecification'
									}, {
										encType: 'application/json',
										method: 'patch'
									})
								}}
								onCustomSpecificationDelete={async (specId) => {
									specsFetcher.submit({
										specId,
										action: 'customSpecification'
									}, {
										encType: 'application/json',
										method: 'delete'
									})
								}}
								onHiddenSpecificationUpdate={async (specId, selected) => {
									specsFetcher.submit({
										specId,
										action: 'hiddenSpecification'
									}, {
										encType: 'application/json',
										method: selected ? 'post' : 'delete'
									})
								}}
								onOrderChange={(specs, customSpecs) => {
									sortSpecifications(customSpecs, specs)
								}}
								onDetailsChange={data => {
									fetcher.submit({
										...data,
										action: 'updateItem'
									}, {
										method: 'post',
										encType: 'application/json',
									})
								}}
								readonly={isRejected || !canUpdateSpecs}
							>
								{!!productPrices && <Button style={{ width: '48%', marginLeft: '1%', marginTop: '10px' }} onClick={() => SetShowPricesDrawer(true)}>View Prices</Button>}
								<FieldWrapper name="quantities" label="Quantity" errors={fetcher.data?.errors}>
									<SelectQuantites
										// mode="tags"
										// placeholder="Select Quantities"
										options={productDetails?.quantities ?? []}
										// style={{
										// 	width: '100%'
										// }}
										min={Math.min(...(productDetails?.quantities ?? [1]))}
										onChange={handleQuantitiesChange}
										// key={(fetcher.json?.quantities || selectedQuantities).map(String)}
										// key="selected_quantities"
										values={(fetcher.json?.quantities || selectedQuantities).map(String)}
										status={getFieldStatus('quantities', fetcher.data?.errors)}
										disabled={!canUpdateQuotation}
									/>
								</FieldWrapper>
							</ProjectItemSpecifications>
							{
								!!productPrices && (
									<Drawer open={showPricesDrawer} onClose={() => SetShowPricesDrawer(false)} destroyOnClose >
										<div className="">
											{productPrices.vendor?.company}
										</div>
										<CmsPricesSelectable
											prices={productPrices.prices}
											selectedQuantities={fetcher.json?.quantities || selectedQuantities}
											currency={ERP_CURRENCY}
											onSelectionChange={(quantities) => {
												if (!isEqual(quantities, selectedQuantities)) {
													handleQuantitiesChange(quantities)
												}
											}}
											disabled={!canUpdateQuotation}
										/>
									</Drawer>
								)
							}
						</div>
					</Panel>
				</Collapse>
				<Collapse defaultActiveKey={['1']} expandIconPosition="end">
					<Panel header={<><CommentOutlined style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: "8px" }} />Comments</>} key="1">
						<Form name="projectitem-comments-form" initialValues={basicItemDetails} onValuesChange={updateItemDetails} form={commentsForm} validationRules={{}} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }} disabled={isRejected}>
							<Form.Item name="internal_comment" label="Internal Comment">
								<Input.TextArea rows={3} disabled={nonUpdatable} />
							</Form.Item>
							<Form.Item name="client_comment" label="Client's Comment">
								<Input.TextArea rows={3} disabled={nonUpdatable} />
							</Form.Item>
							<Form.Item name="vendor_comment" label="Comment for Vendor">
								<Input.TextArea rows={3} disabled={nonUpdatable} />
							</Form.Item>
						</Form>
					</Panel>
				</Collapse>
				<PrintFinisherInvolved
					selectedFinisher={itemDetails.finisher ? itemDetails.selected_finisher : null}
					selectedAddress={itemDetails.finisher ? itemDetails.item_finisher_addresses?.[0] : null}
					finisherComment={itemDetails.finisher ? itemDetails.supplier_comment : null}
					nonUpdatable={nonUpdatable}
				/>
				<Collapse defaultActiveKey={['1']} expandIconPosition="end">
					<Panel header={<><TeamOutlined style={{ marginRight: "8px" }} />Vendors</>} key="1">
						{productDetails.vendors.map(vendor => (
							<VendorItemPrices
								project_id={itemDetails.project_id}
								finisher={itemDetails.finisher ? itemDetails.selected_finisher : null}
								key={vendor.id}
								item={itemId}
								selectedVendorId={itemDetails.vendor_selected_id}
								vendor={{
									...vendor,
									...(productDetails.vendor_price?.[Number(vendor.cms_id)] && { cms_vendor_info: productDetails.vendor_price?.[Number(vendor.cms_id)] }),
									['prices']: itemDetails?.vendors_prices?.[vendor.id] ?? [],
									['quotationAssignment']: itemDetails?.selected_vendors?.find(({ id }) => id === vendor.id) ?? null,
								}}
								quantities={[...selectedQuantities]}
								onVendorSelectionChange={(vendorId, checked) => {
									fetcher.submit({
										action: 'assignVendor',
										vendorId
									}, {
										method: checked ? 'post' : 'delete',
									})
								}}
								onPriceUpdate={updateVendorPrices}
								onVendorCommentChange={handleVendorSpecialCommentChange}
								canUpdateQuotation={canUpdateQuotation}
								disabled={nonUpdatable}
							/>
						))}
					</Panel>
				</Collapse>
				<Collapse defaultActiveKey={['1']} expandIconPosition="end">
					<Collapse.Panel header={<><img src='/icons/headericons/deliverydetails.svg' style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: "8px" }} />Delivery Details</>} key="1">
						<DeliveryAddresses projectId={project.id} itemDetails={itemDetails} customerId={project.customer_id} disabled={nonUpdatable} />
					</Collapse.Panel>
				</Collapse>
				<Collapse defaultActiveKey={['1']} expandIconPosition="end">
					<Collapse.Panel header={<><img src="/icons/headericons/communication.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Order Manager Communication</>} key="1">
						<React.Suspense fallback={<p>Loading order manager communication...</p>}>
							<Await
								resolve={orderCommunicationHistory}
								errorElement={
									<p>Error loading sales order manager history!</p>
								}
							>
								{(messages) => <CommunicationPanel messages={messages} onSubmit={(comment = '') => {
									if (comment.length === 0) {
										message.error('Please enter a comment')
										return false
									}
									fetcher.submit({
										action: 'sendMessageToOrderManager',
										comment
									}, {
										method: 'post'
									})
								}} placeholder='Send message to Order Manager' />}
							</Await>
						</React.Suspense>
					</Collapse.Panel>
				</Collapse>
				<React.Suspense>
					<Await
						resolve={auditTrail}
						errorElement={
							<p>Error loading audit trail!</p>
						}
					>
						{(logs) => (
							<Collapse defaultActiveKey={['1']} expandIconPosition="end">
								<Panel header={<><img src="/icons/headericons/audittrail.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Audit Trail</>} key="1" >
									<Table
										columns={ItemUpdateAuditTrailColumns}
										dataSource={logs}
										rowKey="id"
									/>
								</Panel>
							</Collapse>
						)}
					</Await>
				</React.Suspense>
			</div>
		</Page >
	)
}

ProjectItemUpdate.propTypes = {
	title: PropTypes.string.isRequired
}

ProjectItemUpdate.Actions = {
	assignVendor: async ({ params, data, method }) => {
		if (method.toLowerCase() === 'delete') {
			return removeProjectItemVendor(params.itemId, data.vendorId)
		}
		else {
			try {
				const { message: msg } = await assignProjectItemVendor(params.itemId, data.vendorId)
				message.success(msg)
				return true
			}
			catch (error) {
				message.error(error.message)
				return false
			}
		}
	},
	sendQuoationRequest: ({ params, data }) => {
		sendVendorProjectItemQuotationRequest(params.itemId, data.vendorId)
			.then(() => {
				message.success('Quotation Request sent to Vendor')
			})
			.catch((err) => {
				message.error(err.message)
			})
		return null
	},
	updateItem: async ({ params, data }) => {
		const { itemId } = params

		try {
			await updateProjectItem(itemId, data)
			return true
			// if(data?.quantities?.every(el => el < 100000) || data?.quantities?.length!=0){
			// 	await updateProjectItem(itemId, data)
			// 	return true
			// }
			// else {
			// 	message.warning('Quantities must be less than 100000')
			// }

		}
		catch (error) {
			message.error('Unable to update item')
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}
		return false
	},
	assignFinisher: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { addressId, finisherId } = data
			// await updateProjectItem(itemId, { finisher: 1 })
			const msg = await assignFinisher(itemId, finisherId, addressId)
			message.success(msg)
			return true
		}
		catch (error) {
			console.log("error", error);
			message.error(error.message)

		}
		return false
	},
	removeFinisher: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { finisherId } = data
			const msg = await unassignFinisher(itemId, finisherId)
			message.success(msg)
			return true
		}
		catch (error) {
			console.log("error", error);
			message.error(error.message)

		}
		return false
	},
	updateVendorPrice: async ({ params, data }) => {
		const { itemId } = params
		try {
			let {
				vendorId,
				quantity,
				purchase_price,
				purchase_price_supplier = null,
				finisher_price,
				finisher_price_supplier = null,
				handling_charge,
				handling_charge_supplier = null,
				shipping_charge,
				shipping_charge_supplier = null,
				quotation_price_reference,
				margin,
				custom_selling_price_unit
			} = data

			if (purchase_price_supplier) {
				purchase_price = purchase_price_supplier
				handling_charge = handling_charge_supplier
				finisher_price = finisher_price_supplier ?? null
				shipping_charge = shipping_charge_supplier
			}


			await updateProjectItemVendorPrice(itemId, vendorId, quantity, {
				purchase_price,
				finisher_price,
				handling_charge,
				shipping_charge,
				margin,
				quotation_price_reference,
				custom_selling_price_unit
			})
			return true
		}
		catch (error) {
			message.error('Unable to update item')
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}
		return false
	},
	updateSelectedSpecs: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { selectedSpecs } = data
			const payload = Object.entries(selectedSpecs).map(([key, { valueId, sort_order }]) => ({
				spec_id: Number(key),
				spec_value_id: Number(valueId),
				sort_order: Number(sort_order),
			}))
			await updateProjectItemSpecifications(itemId, payload)
			return true
		}
		catch (error) {
			message.error(error.message)
		}
		return false
	},
	customSpecification: async ({ params, data, method }) => {
		const { itemId } = params
		try {
			if (method.toUpperCase() === 'PATCH') {
				const { specId, ...specData } = data
				await updateItemCustomSpecification(specId, specData)
				//return true
			}
			else if (method.toUpperCase() === 'DELETE') {
				const { specId } = data
				await deleteItemCustomSpecification(specId)
				// return true
			}
			else {
				await addItemCustomSpecification(itemId, data)
				//return true
			}
			return true

		}
		catch (error) {
			message.error(error.message)
			if (error instanceof ValidationError) {
				return {
					error: error.errors
				}
			}
		}
		return false
	},
	sortSpecifications: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { sortSpecs = [], selectedSpecs } = data
			const updateSpecsPayload = Object.entries(selectedSpecs).map(([key, { valueId, sort_order }]) => ({
				spec_id: Number(key),
				spec_value_id: Number(valueId),
				sort_order: Number(sort_order),
			}))
			await updateProjectItemSpecifications(itemId, updateSpecsPayload)
			await sortItemCustomSpecifications(itemId, sortSpecs.map(({ id, sort_order }) => ({ id, sort_order })))
			return true
		}
		catch (error) {
			message.error(error.message)
		}
		return false
	},
	hiddenSpecification: async ({ params, data, method }) => {
		const { itemId } = params
		try {
			const { specId } = data
			if (method.toUpperCase() === 'POST') {
				await addItemHiddenSpecification(itemId, specId)
			}
			else if (method.toUpperCase() === 'DELETE') {
				await deleteItemHiddenSpecification(itemId, specId)
			}
			return true
		}
		catch (error) {
			message.error(error.message)
			if (error instanceof ValidationError) {
				return {
					error: error.errors
				}
			}
		}
		return false
	},
	sendMessageToOrderManager: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { comment } = data
			const msg = await sendCommentToOrderManager(itemId, comment)
			message.success(msg)
			return true
		}
		catch (error) {
			message.error(error.message)
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
			return false
		}
	},
	updateDeliveryAddress: async ({ params, data }) => {
		const { itemId } = params
		try {
			// const payload = Object.values(data.address).map(address => convertDataTypes(address, {
			// 	quantity: parseInt,
			// 	address_id: parseInt,
			// 	additional_cost: parseFloat,
			// 	margin: parseFloat
			// }))
			const { deliveryId, ...payload } = convertDataTypes(data, {
				deliveryId: parseInt,
				quantity: parseInt,
				address_id: parseInt,
				additional_cost: parseFloat,
				margin: parseFloat
			})
			await updateItemDeliveryAddress(itemId, deliveryId, payload)
			return true
		}
		catch (error) {
			message.error(`Unable to update delivery address.(${error.message})`)
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}
		return false
	},
	addDeliveryAddress: async ({ params, data }) => {
		const { itemId } = params
		try {
			const payload = convertDataTypes(data, {
				quantity: parseInt,
				address_id: parseInt,
				additional_cost: parseFloat,
				margin: parseFloat
			})
			await addItemDeliveryAddress(itemId, payload)
			return true
		}
		catch (error) {
			message.error(`Unable to add delivery address.(${error.message})`)
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}
		return false
	},
	saveBulkDeliveryAddress: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { additional_cost, notes, margin, file_id } = convertDataTypes(data, {
				additional_cost: Number,
				margin: Number,
			})

			let fileId = null
			if (file_id && (file_id instanceof File) && file_id.name.length > 0) { // Upload file if selected
				if (file_id.size === 0) {
					throw new ValidationError('Invalid file', [
						{
							path: 'file_id',
							message: 'Upload valid file'
						}
					])
				}
				//Upload file
				fileId = await uploadFileFromObject(file_id)
				if (!fileId) {
					throw new Error('Unable to upload file')
				}
			}

			const msg = await bulkItemDeliveryAddress(itemId, additional_cost, notes, margin, fileId)
			message.success(msg);
			return true
		}
		catch (error) {
			message.error(error.message)
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}

		try {
			const payload = convertDataTypes(data, {
				quantity: parseInt,
				address_id: parseInt,
				additional_cost: parseFloat,
				margin: parseFloat
			})
			await addItemDeliveryAddress(itemId, payload)
			return true
		}
		catch (error) {
			message.error(`Unable to add delivery address.(${error.message})`)
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}
		return false
	},
	removeBulkDeliveryFile: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { fileId } = convertDataTypes(data, {
				deliveryId: parseInt
			})
			await bulkItemDeliveryAddress(itemId, 0, "", 0, fileId) // Reset additional_cost, notes, margin, and fileId
			const msg = await deleteBulkDeliveryFile(itemId, fileId)
			message.success(msg);
			return true
		} catch (error) {
			message.error(`Unable to delete bulk delivery file. (${error.message})`)

			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}
		return false;
	},
	removeDeliveryAddress: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { deliveryId } = convertDataTypes(data, {
				deliveryId: parseInt
			})
			await deleteItemDeliveryAddress(itemId, deliveryId)
			return true
		}
		catch (error) {
			message.error(`Unable to delete delivery address.(${error.message})`)
			if (error instanceof ValidationError) {
				return {
					errors: error.errors
				}
			}
		}
		return false
	},
	deleteItem: async ({ params }) => {
		const { itemId } = params
		try {
			await deleteItem(itemId)
			message.success('Item Deleted successfully')
			return redirect('..', { replace: true })
		}
		catch (error) {
			message.error(error.message)
		}
		return false
	},
	declineAlert: async ({ params, data }) => {
		const { itemId } = params
		try {
			const { alertId, comment = '' } = data
			const msg = await declineAlert(itemId, alertId, comment)
			message.success(msg)
			return true
		}
		catch (error) {
			message.error(error.message)
			return false
		}
	},
}

ProjectItemUpdate.Loader = async ({ params }) => {
	const { project, ...itemDetails } = await getProjectItem(params.itemId)
	if (!itemDetails) {
		throw new Error('Invalid Project Item')
	}

	let productPrices = null
	if (itemDetails.price_available) {
		productPrices = await getItemPrices(params.itemId)
		if (isObjectEmpty(productPrices)) {
			productPrices = null
		}
	}

	const basicItemDetails = (({
		expected_delivery_period, delivery_date, client_expected_delivery_date, quantities, internal_comment, client_comment, vendor_comment
	}) => ({
		expected_delivery_period, delivery_date, client_expected_delivery_date, quantities, internal_comment, client_comment, vendor_comment
	}))(itemDetails)

	const projectDetails = await getProjectDetails(itemDetails.project_id)

	const selectedQuantities = Array.isArray(basicItemDetails.quantities) ? basicItemDetails.quantities.map(Number) : []

	const productDetails = await getProductDetails(itemDetails.product_id, project.cms_language_code, project.cms_language_content_code)
	if (!productDetails) {
		throw new Error('Invalid Product')
	}

	const auditTrail = getProjectItemActivityLogs(itemDetails.id)

	const orderCommunicationHistory = getItemOrderSalesCommentHistory(params.itemId)

	return defer({
		itemId: itemDetails.id,
		productId: itemDetails.product_id,
		itemDetails,
		basicItemDetails,
		productDetails,
		selectedQuantities,
		auditTrail,
		productPrices,
		currency: projectDetails.currency,
		language: projectDetails.language,
		orderCommunicationHistory,
		project: { ...project, ...projectDetails }
	})
}

export default ProjectItemUpdate